<template>
  <div>
    <!-- Date range picker -->
    <calendar-monthly-bar
        v-model="selectedMonths"
        @input="onMonthSelected"
        :max-options-size="6"
        :future-options-size="3"
    />
    <!-- Summary Cards -->
    <b-row>
      <b-col cols="12" lg="4">
        <statistic-card-horizontal icon="fas fa-sign-in-alt" v-bind:statistic="`${stats.totalServices.total || 0}`"
                                   v-bind:loading="loadingStats"
                                   :statistic-title="$t('providerStats.services')"
                                   icon-type="fontawesome" color="primary">
          <template v-slot:footer>
            <span class="text-secondary" >{{$t('providerStats.completedServices')}}: {{stats.totalServices.completed || 0}}</span>
          </template>
        </statistic-card-horizontal>
      </b-col>
      <b-col cols="12" lg="4">
        <statistic-card-horizontal icon="fa-solid fa-receipt" v-bind:statistic="`${stats.totalExpenses.paid || 0} €`" v-bind:loading="loadingStats"
                                   :statistic-title="$t('providerStats.expenses')"
                                   icon-type="fontawesome" color="warning">
          <template v-slot:footer>
            <span class="text-secondary" >{{$t('providerStats.pendingExpenses')}}: {{stats.totalExpenses.pending || 0}} €</span>
          </template>
        </statistic-card-horizontal>
      </b-col>
      <b-col cols="12" lg="4">
        <statistic-card-horizontal icon="fa-solid fa-chart-line" v-bind:statistic="`${stats.totalRevenue || 0} €`" v-bind:loading="loadingStats"
                                   :statistic-title="$t('providerStats.revenue')"
                                   icon-type="fontawesome" color="success">
          <template v-slot:footer>
            <span class="text-secondary" >{{$t('providerStats.contracts')}}: {{stats.totalContracts}}</span>
          </template>
        </statistic-card-horizontal>
      </b-col>
    </b-row>

    <!-- Status Breakdown & Units Sold by Product Charts -->
    <b-row class="mt-1">
      <b-col cols="12" md="4">
        <b-card>
          <h5>{{ $t('Revenue breakdown') }}</h5>
          <provider-revenue-donut-chart
              :data="statusChartData"
              :loading="false"
          />
        </b-card>
      </b-col>

      <b-col cols="12" md="8">
        <b-card>
          <h5>{{ $t('Revenue by services') }}</h5>
          <vue-apex-charts
              type="bar"
              height="350"
              :options="unitsChartOptions"
              :series="unitsSeries"
          />
        </b-card>
      </b-col>
    </b-row>

    <!-- Top Products Table -->
    <b-row class="">
      <b-col cols="12">
        <b-card>
          <h5>{{ $t('Services') }}</h5>
          <b-table :items="stats.services" :fields="topFields" small responsive>
            <template #cell(name)="{ value, item }">
              <b-link @click="()=>onRowClick(item)">{{value}}</b-link>
            </template>
            <template #cell(totalExpenses)="{ value }">€{{ value.toFixed(2) }}</template>
            <template #cell(totalPendingExpenses)="{ value }">€{{ value.toFixed(2) }}</template>
            <template #cell(totalRevenue)="{ value }">€{{ value.toFixed(2) }}</template>
          </b-table>
        </b-card>
      </b-col>
    </b-row>
    <provider-services-table-modal ref="providerServicesTableModal"/>
  </div>
</template>

<script>
import CalendarMonthlyBar from '@/components/widgets/calendar/CalendarMonthlyBar.vue';
import StatisticCardHorizontal from '@core/components/statistics-cards/StatisticCardHorizontal.vue';
import VueApexCharts from 'vue-apexcharts';
import {mapActions, mapGetters} from "vuex";
import ProviderRevenueDonutChart from "@/components/chart/providers/ProviderRevenueDonutChart.vue";
import ProviderServicesTableModal from "@/components/modals/providers/ProviderServicesTableModal.vue";

const statsTemplate = () => ({
  totalRevenue: 0,
  totalContracts: 0,
  totalServices: {total: 0, completed: 0},
  totalExpenses: {pending:0, paid:0},
  statusCounts: {REVENUE: 0, PENDING_EXPENSES: 0, EXPENSES: 0},
  services: [],
  servicesProviders: {},
});
export default {
  name: 'providersDashboard',
  components: {
    ProviderServicesTableModal,
    CalendarMonthlyBar,
    StatisticCardHorizontal,
    ProviderRevenueDonutChart,
    VueApexCharts
  },
  data() {
    return {
      loadingStats: false,
      buildings: [],

      selectedMonths: {start: null, end: null},
      stats: statsTemplate(),

    }
  },
  computed: {
    ...mapGetters('appConfig', ['isDarkMode']),
    topFields() {
      return [
        {key: 'name', label: this.$t('name')},
        {key: 'totalServices', label: this.$t('providerStats.services')},
        {key: 'totalContracts', label: this.$t('providerStats.contracts')},
        {key: 'totalExpenses', label: this.$t('providerStats.expenses')},
        {key: 'totalPendingExpenses', label: this.$t('providerStats.pendingExpenses')},
        {key: 'totalRevenue', label: this.$t('providerStats.revenue')},
      ]
    },
    statusChartData() {
      return Object.entries(this.stats.statusCounts).map(([name, value]) => ({name, value}));
    },
    unitsSeries() {
      //revenue: 0, pending_expenses: 0, expenses: 0
      return [
          {name: 'Revenue', data: Object.values(this.stats.servicesProviders).map(item => item.revenue), color: '#27ae60'},
          {name: 'Pending Expenses', data: Object.values(this.stats.servicesProviders).map(item => item.pending_expenses), color: '#f1c40f'},
          {name: 'Expenses', data: Object.values(this.stats.servicesProviders).map(item => item.expenses), color: '#95a5a6'},
      ];
    },
    unitsChartOptions() {
      let $this = this;
      return {
        chart: {
          type: 'bar',
          height: 350,
          stacked: true,
          toolbar: {
            show: true
          },
          zoom: {
            enabled: true
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0
            }
          }
        }],
        plotOptions: {
          bar: {
            horizontal: false,
            borderRadius: 10,
            borderRadiusApplication: 'end', // 'around', 'end'
            borderRadiusWhenStacked: 'last', // 'all', 'last'
            dataLabels: {
              total: {
                enabled: true,
                style: {
                  fontSize: '13px',
                  fontWeight: 900
                }
              }
            }
          },
        },
        xaxis: {
          // type: 'datetime',
          // categories: ['01/01/2011 GMT', '01/02/2011 GMT', '01/03/2011 GMT', '01/04/2011 GMT', '01/05/2011 GMT', '01/06/2011 GMT'],
          categories: Object.keys(this.stats.servicesProviders),
        },
        legend: {
          position: 'right',
          offsetY: 40
        },
        fill: {
          opacity: 1
        }
      }
    }

  },
  created() {
    this.$root.$on('crm::branch-updated', this.refresh);
  },
  beforeDestroy() {
    this.$root.$off('crm::branch-updated', this.refresh);
  },
  methods: {
    ...mapActions('providersServices', ['fetchProviderServicesStats']),

    onMonthSelected(month) {
      this.selectedMonths = month;
      this.refresh();
    },
    refresh() {
      let $this = this;
      this.stats.loading = true;
      const {start, end} = this.selectedMonths;
      let buildings = this.buildings;
      this.fetchProviderServicesStats({serviceTime:{start, end}, buildings})
          .then(statsList => {
            console.log("refresh provider services stats", statsList);
            let template = statsTemplate();
            if(statsList == null || statsList.length === 0) {
              $this.stats = template;
              return;
            }
            statsList.forEach(s=>s.totalContracts = s.contracts?.length || 0);
            template.services = statsList;
            template.totalContracts = statsList.reduce((sum, item) => sum + (item.contracts?.length || 0), 0);
            template.totalRevenue = statsList.reduce((sum, item) => sum + item.totalRevenue, 0);
            template.totalServices = {
              total: statsList.reduce((sum, item) => sum + item.totalServices, 0),
              completed: statsList.reduce((sum, item) => sum + item.totalCompletedServices, 0)
            };
            template.totalExpenses = statsList.reduce((acc, item) => {
              acc.pending += item.totalPendingExpenses;
              acc.paid += item.totalExpenses;
              return acc;
            }, {pending: 0, paid: 0});
            template.statusCounts = statsList.reduce((acc, item) => {
              acc.REVENUE += item.totalRevenue;
              acc.PENDING_EXPENSES += item.totalPendingExpenses || 0;
              acc.EXPENSES += item.totalExpenses;
              return acc;
            }, {REVENUE: 0, PENDING_EXPENSES: 0, EXPENSES: 0});
            template.servicesProviders = statsList.reduce((acc, item) => {
              if(!acc[item.name]) {
                acc[item.name] = {revenue: 0, pending_expenses: 0, expenses: 0};
              }
              acc[item.name].revenue += item.totalRevenue;
              acc[item.name].pending_expenses += item.totalPendingExpenses || 0;
              acc[item.name].expenses += item.totalExpenses;
              return acc;
            }, {});
            $this.stats = template;
          })
          .finally(() => {
            $this.stats.loading = true;
          });
    },
    onRowClick(service) {
      console.log("Row clicked", service);
      let filter = {
        providerId: [service.providerId],
        serviceTime: {start: this.selectedMonths.start, end: this.selectedMonths.end}
      };
      this.$refs.providerServicesTableModal.show(filter);
    }
  },

};
</script>

<style scoped>
h5 {
  font-weight: 600;
}
</style>
